import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Power, Search, Pencil } from 'lucide-react';

const Users = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'cajero',
    pin_vendedor: '',
    pin_admin: '',
  });
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    role: 'cajero',
    password: '',
    pin_vendedor: '',
    pin_admin: '',
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Email es opcional: si está vacío no lo enviamos
      const payload = { ...formData };
      if (!payload.email?.trim()) delete payload.email;
      if (!payload.pin_vendedor?.trim()) delete payload.pin_vendedor;
      if (!payload.pin_admin?.trim()) delete payload.pin_admin;
      await axios.post(`${API}/auth/register`, payload);
      toast.success('Usuario creado exitosamente');
      setDialogOpen(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'cajero',
        pin_vendedor: '',
        pin_admin: '',
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditFormData({
      full_name: u.full_name || '',
      email: u.email || '',
      role: u.role,
      password: '',
      pin_vendedor: u.pin_vendedor || '',
      pin_admin: u.pin_admin || '',
      is_active: !!u.is_active,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      const payload = {
        full_name: editFormData.full_name,
        email: editFormData.email || '',
        role: editFormData.role,
        is_active: editFormData.is_active,
        pin_vendedor: editFormData.pin_vendedor || '',
        pin_admin: editFormData.pin_admin || '',
      };
      if (editFormData.password?.trim()) {
        payload.password = editFormData.password;
      }
      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success('Usuario actualizado');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}/toggle-status`);
      toast.success('Estado actualizado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleLabels = {
    admin: 'Administrador',
    cajero: 'Cajero',
    inventario: 'Encargado de Inventario',
    gerente: 'Gerente',
    contador: 'Contador',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800">Usuarios</h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">Gestiona los usuarios del sistema</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto" data-testid="create-user-btn">
              <UserPlus className="mr-2" size={18} />
              <span className="text-sm md:text-base">Crear Usuario</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Usuario</DialogTitle>
              <DialogDescription>Complete la información del nuevo usuario</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  data-testid="form-username-input"
                />
              </div>
              <div>
                <Label htmlFor="full_name">Nombre Completo</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="form-fullname-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email <span className="text-xs text-slate-400">(opcional)</span></Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="form-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="form-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pin_vendedor">PIN Vendedor <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <Input
                    id="pin_vendedor"
                    inputMode="numeric"
                    maxLength={8}
                    value={formData.pin_vendedor}
                    onChange={(e) => setFormData({ ...formData, pin_vendedor: e.target.value.replace(/\D/g, '') })}
                    placeholder="ej: 1234"
                    data-testid="form-pin-vendedor-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pin_admin">PIN Admin/Seguridad <span className="text-xs text-slate-400">(opcional)</span></Label>
                  <Input
                    id="pin_admin"
                    inputMode="numeric"
                    maxLength={8}
                    value={formData.pin_admin}
                    onChange={(e) => setFormData({ ...formData, pin_admin: e.target.value.replace(/\D/g, '') })}
                    placeholder="ej: 9999"
                    data-testid="form-pin-admin-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="role">Rol</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="form-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="cajero">Cajero</SelectItem>
                    <SelectItem value="inventario">Encargado de Inventario</SelectItem>
                    <SelectItem value="gerente">Gerente</SelectItem>
                    <SelectItem value="contador">Contador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-user-btn">
                Crear Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar usuarios..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-users-input"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {filteredUsers.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.username}`}>
                <td className="font-medium">{u.username}</td>
                <td>{u.full_name}</td>
                <td className="text-slate-600">{u.email}</td>
                <td>
                  <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                    {roleLabels[u.role]}
                  </span>
                </td>
                <td>
                  <span
                    className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                      u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {u.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(u)}
                      data-testid={`edit-user-btn-${u.username}`}
                      title="Editar usuario"
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(u.id)}
                      disabled={u.id === user?.id}
                      data-testid={`toggle-status-btn-${u.username}`}
                      title={u.is_active ? 'Inactivar usuario' : 'Activar usuario'}
                    >
                      <Power size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron usuarios
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              {editingUser ? `Modificar datos de @${editingUser.username}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label htmlFor="edit_full_name">Nombre Completo</Label>
              <Input
                id="edit_full_name"
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                required
                data-testid="edit-fullname-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email <span className="text-xs text-slate-400">(opcional)</span></Label>
              <Input
                id="edit_email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <Label htmlFor="edit_role">Rol</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="cajero">Cajero</SelectItem>
                  <SelectItem value="inventario">Encargado de Inventario</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="contador">Contador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_password">Nueva Contraseña <span className="text-xs text-slate-400">(dejar vacío para no cambiar)</span></Label>
              <Input
                id="edit_password"
                type="password"
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="edit-password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit_pin_vendedor">PIN Vendedor</Label>
                <Input
                  id="edit_pin_vendedor"
                  inputMode="numeric"
                  maxLength={8}
                  value={editFormData.pin_vendedor}
                  onChange={(e) => setEditFormData({ ...editFormData, pin_vendedor: e.target.value.replace(/\D/g, '') })}
                  placeholder="ej: 1234"
                  data-testid="edit-pin-vendedor-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_pin_admin">PIN Admin/Seguridad</Label>
                <Input
                  id="edit_pin_admin"
                  inputMode="numeric"
                  maxLength={8}
                  value={editFormData.pin_admin}
                  onChange={(e) => setEditFormData({ ...editFormData, pin_admin: e.target.value.replace(/\D/g, '') })}
                  placeholder="ej: 9999"
                  data-testid="edit-pin-admin-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Cada PIN debe ser único en el sistema. Deje el campo vacío para eliminar el PIN asignado.
            </p>
            <Button
              type="submit"
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              data-testid="submit-edit-user-btn"
            >
              Guardar Cambios
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
